
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/components/ThemeProvider";
import { Logo } from "./Logo";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import {
  LayoutDashboard,
  FileText, Layers, Tag, DollarSign, Users, Grid3X3, Shield, Upload, 
  Database, Settings, Package, FileSearch, Link, BookOpen, Sliders,
  BarChart3, GitBranch, TrendingDown, Zap, HeartPulse, FileSignature, ClipboardCheck,
  Building2
} from "lucide-react";

interface MobileMenuProps {
  onOpenChange: (open: boolean) => void;
}

interface MobileNavLinkProps {
  to: string;
  title: string;
  active: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
}

function MobileNavLink({ to, title, active, onClick, icon }: MobileNavLinkProps) {
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center text-sm font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] border border-transparent hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-soft ${
          isActive 
            ? "bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 shadow-inner-soft border-primary-200 dark:border-primary-700" 
            : is24Seven 
              ? "text-24seven-text hover:bg-gradient-to-r hover:from-primary-50 hover:to-neutral-50 hover:text-primary-700" 
              : "text-muted-foreground hover:bg-gradient-to-r hover:from-primary-50 hover:to-neutral-50 hover:text-primary-700"
        }`
      }
    >
      {icon && (
        <div className="mr-3 p-1 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 group-hover:from-primary-200 group-hover:to-primary-300 dark:group-hover:from-primary-800/50 dark:group-hover:to-primary-700/50 transition-all duration-200">
          <span className="text-primary-600 dark:text-primary-400">{icon}</span>
        </div>
      )}
      <span className="font-medium">{title}</span>
    </NavLink>
  );
}

export function MobileMenu({ onOpenChange }: MobileMenuProps) {
  const location = useLocation();
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";
  const { useHasRoleQuery } = useUserRoles();
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  const { canUseDocumentExtraction, canUseSalesAnalytics, canUseCostOptimization, canUseDataHealth, canUseGoverningDocuments } = useFeatureAccess();
  
  // Check if any premium tools are available
  const hasAnyPremiumTools = canUseSalesAnalytics || canUseCostOptimization || canUseDataHealth || canUseGoverningDocuments;
  
  const hasAdminAccess = isAdmin || isDeveloper;
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-2">
        <Logo isAuthenticated={true} />
      </div>

      <ScrollArea className="flex-1 overflow-auto">
        <div className="space-y-6 py-6">
          {/* Main Navigation Section */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg">
                <LayoutDashboard className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                Main Navigation
              </h2>
            </div>
            <div className="space-y-2">
              <MobileNavLink 
                to="/" 
                title="Dashboard" 
                active={location.pathname === "/"} 
                onClick={() => onOpenChange(false)}
                icon={<LayoutDashboard className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contracts" 
                title="Contracts" 
                active={isActive("/contracts") && !isActive("/contracts/import")} 
                onClick={() => onOpenChange(false)}
                icon={<FileText className="h-4 w-4" />}
              />

              <MobileNavLink 
                to="/contracts/import" 
                title="Import Contracts" 
                active={isActive("/contracts/import")} 
                onClick={() => onOpenChange(false)}
                icon={<Upload className="h-4 w-4" />}
              />
              
              {canUseDocumentExtraction && (
                <MobileNavLink 
                  to="/documents/extraction" 
                  title="Document Extraction" 
                  active={isActive("/documents/extraction")} 
                  onClick={() => onOpenChange(false)}
                  icon={<FileSearch className="h-4 w-4" />}
                />
              )}
              
              <MobileNavLink 
                to="/services" 
                title="Services" 
                active={isActive("/services")} 
                onClick={() => onOpenChange(false)}
                icon={<Package className="h-4 w-4" />}
              />
            </div>
          </div>
          
          <div className="px-4">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-600 to-transparent"></div>
          </div>
          {/* Management Section */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg">
                <Settings className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                Setup & Manage
              </h2>
            </div>
            <div className="space-y-2">
              <MobileNavLink 
                to="/categories" 
                title="Categories" 
                active={isActive("/categories")} 
                onClick={() => onOpenChange(false)}
                icon={<Tag className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contract-types" 
                title="Contract Types" 
                active={isActive("/contract-types")} 
                onClick={() => onOpenChange(false)}
                icon={<Layers className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/currencies" 
                title="Currencies" 
                active={isActive("/currencies")} 
                onClick={() => onOpenChange(false)}
                icon={<DollarSign className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/suppliers" 
                title="Vendors" 
                active={isActive("/suppliers")} 
                onClick={() => onOpenChange(false)}
                icon={<Building2 className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contacts" 
                title="Contacts" 
                active={isActive("/contacts")} 
                onClick={() => onOpenChange(false)}
                icon={<Users className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/groups" 
                title="Departments" 
                active={isActive("/groups")} 
                onClick={() => onOpenChange(false)}
                icon={<Grid3X3 className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/settings/custom-fields" 
                title="Custom Fields" 
                active={isActive("/settings/custom-fields")} 
                onClick={() => onOpenChange(false)}
                icon={<Sliders className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/contract-templates" 
                title="Document Templates" 
                active={isActive("/contract-templates")} 
                onClick={() => onOpenChange(false)}
                icon={<FileSignature className="h-4 w-4" />}
              />
              
              <MobileNavLink 
                to="/clause-library" 
                title="Clause Library" 
                active={isActive("/clause-library")} 
                onClick={() => onOpenChange(false)}
                icon={<BookOpen className="h-4 w-4" />}
              />
            </div>
          </div>
          
          {/* Tools Section - Only show if user has access to premium tools */}
          {hasAnyPremiumTools && (
            <>
              <div className="px-4">
                <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-600 to-transparent"></div>
              </div>
              
              <div className="px-4">
                <div className="mb-4 flex items-center space-x-2">
                  <div className="p-1.5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                    Tools
                  </h2>
                </div>
                <div className="space-y-2">
                  {canUseSalesAnalytics && (
                    <MobileNavLink 
                      to="/sales-analytics" 
                      title="Sales Analytics" 
                      active={isActive("/sales-analytics")} 
                      onClick={() => onOpenChange(false)}
                      icon={<BarChart3 className="h-4 w-4" />}
                    />
                  )}
                  
                  {canUseSalesAnalytics && (
                    <MobileNavLink 
                      to="/approval-workflows" 
                      title="Approval Workflows" 
                      active={isActive("/approval-workflows")} 
                      onClick={() => onOpenChange(false)}
                      icon={<GitBranch className="h-4 w-4" />}
                    />
                  )}
                  
                  {canUseCostOptimization && (
                    <MobileNavLink 
                      to="/cost-optimization" 
                      title="Cost Optimization" 
                      active={isActive("/cost-optimization")} 
                      onClick={() => onOpenChange(false)}
                      icon={<TrendingDown className="h-4 w-4" />}
                    />
                  )}
                  
                  {canUseDataHealth && (
                    <MobileNavLink 
                      to="/data-health" 
                      title="Data Health" 
                      active={isActive("/data-health")} 
                      onClick={() => onOpenChange(false)}
                      icon={<HeartPulse className="h-4 w-4" />}
                    />
                  )}

                  {canUseDataHealth && (
                    <MobileNavLink 
                      to="/portfolio-assessment" 
                      title="Portfolio Assessment" 
                      active={isActive("/portfolio-assessment")} 
                      onClick={() => onOpenChange(false)}
                      icon={<ClipboardCheck className="h-4 w-4" />}
                    />
                  )}

                  {canUseGoverningDocuments && (
                    <MobileNavLink 
                      to="/governing-documents" 
                      title="Governing Documents" 
                      active={isActive("/governing-documents")} 
                      onClick={() => onOpenChange(false)}
                      icon={<FileText className="h-4 w-4" />}
                    />
                  )}
                </div>
              </div>
            </>
          )}
          
          <div className="px-4">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-600 to-transparent"></div>
          </div>
          
          {/* Personal Section */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
                <Settings className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                Account & Settings
              </h2>
            </div>
            <div className="space-y-2">
              <MobileNavLink 
                to="/settings" 
                title="Settings" 
                active={isActive("/settings")} 
                onClick={() => onOpenChange(false)}
                icon={<Settings className="h-4 w-4" />}
              />

              <MobileNavLink 
                to="/settings/integrations" 
                title="ERP Integrations" 
                active={isActive("/settings/integrations")} 
                onClick={() => onOpenChange(false)}
                icon={<Link className="h-4 w-4" />}
              />

              <MobileNavLink 
                to="/documentation" 
                title="Documentation" 
                active={isActive("/documentation")} 
                onClick={() => onOpenChange(false)}
                icon={<BookOpen className="h-4 w-4" />}
              />
            </div>
          </div>

          {hasAdminAccess && (
            <>
              <div className="px-4">
                <div className="h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-600 to-transparent"></div>
              </div>
              
              <div className="px-4">
                <div className="mb-4 flex items-center space-x-2">
                  <div className="p-1.5 bg-gradient-to-r from-red-500 to-red-600 rounded-lg">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-lg font-semibold tracking-tight bg-gradient-to-r from-neutral-900 to-neutral-600 dark:from-neutral-100 dark:to-neutral-300 bg-clip-text text-transparent">
                    Administration
                  </h2>
                </div>
                <div className="space-y-2">
                  <MobileNavLink 
                    to="/admin" 
                    title="Admin Panel" 
                    active={isActive("/admin")} 
                    onClick={() => onOpenChange(false)}
                    icon={<Shield className="h-4 w-4" />}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
